<template>
  <v-container grid-list-md fluid>
    <v-layout wrap justify-space-between>
      <v-flex xs12 sm2>
        <v-btn
          color="primary"
          @click="create"
          class="mb-4"
          :disabled="!hasCondominioAccess(entity, 'create')"
        >
          {{ this.translate("system.button.add.title") }}
          <v-icon dark>{{ this.translate("system.button.add.icon") }}</v-icon>
        </v-btn>
      </v-flex>
      <v-flex xs12 sm4>
        <v-text-field
          append-icon="search"
          label="Pesquisar"
          v-model="search"
          @change="updateSum"
        ></v-text-field>
      </v-flex>
      <v-flex xs12 sm1>
        <v-menu offset-y>
          <v-btn slot="activator" icon class="ml-2" title="Relatórios">
            <v-icon>description</v-icon>
          </v-btn>
          <v-list>
            <!-- PDF -->
            <v-list-tile @click="exportReport(1)">
              <v-list-tile-action>
                <v-icon color="red darken-1">picture_as_pdf</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Relatório em PDF</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Excel -->
            <v-list-tile @click="exportReport(2)">
              <v-list-tile-action>
                <v-icon color="green darken-2">table_chart</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Relatório em Excel</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-flex>

      <!--Index-->
      <v-flex xs12>
        <v-data-table
          ref="RendimentoIsentoTable"
          :headers="headers"
          :items="items"
          :search="search"
          class="elevation-1"
          :rows-per-page-items="[100, 50, 25, { text: 'Todos', value: -1 }]"
          rows-per-page-text="Registros por página"
          no-data-text="Não existem registros"
          no-results-text="Nenhum registro encontrado"
        >
          <template slot="items" slot-scope="props">
            <td>{{ props.item.sequencia_ir }}</td>
            <td>{{ props.item.id_texto }}</td>
            <td>{{ getCodigoText(props.item.codigo) }}</td>
            <td>{{ props.item.cnpj | formatCpfCnpj | formatCpfCnpj }}</td>
            <td align="right">
              {{ props.item.total_valor | formatCurrency }}
            </td>
            <td align="right" v-if="particular">
              {{ props.item.total_decimo_terceiro | formatCurrency }}
            </td>
            <td align="right" v-if="particular">
              {{ props.item.total_irrf | formatCurrency }}
            </td>
            <td align="right" v-if="particular">
              {{ props.item.total_irrf_decimo_terceiro | formatCurrency }}
            </td>
            <td align="right" v-if="particular">
              {{ props.item.total_contribuicao_previdenciaria | formatCurrency }}
            </td>
            <td v-if="temDependente">
              {{ props.item.dependente ? props.item.dependente.id_texto : "" }}
            </td>
            <td align="center" style="min-width: 150px">
              <v-btn
                icon
                class="mx-0"
                @click="editItem(props.item)"
                :disabled="!hasCondominioAccess(entity, 'update')"
              >
                <v-icon color="teal">edit</v-icon>
              </v-btn>
              <v-btn
                icon
                class="mx-0"
                @click="deleteItem(props.item)"
                :disabled="!hasCondominioAccess(entity, 'delete')"
              >
                <v-icon color="pink">delete</v-icon>
              </v-btn>
            </td>
          </template>
          <template v-slot:footer>
            <td colspan="4">
              <strong>Total Geral</strong>
            </td>
            <td align="right">
              <strong>R$ {{ sum.total_valor | formatDouble }}</strong>
            </td>
            <td align="right" v-if="particular">
              <strong>R$ {{ sum.total_decimo_terceiro | formatDouble }}</strong>
            </td>
            <td align="right" v-if="particular">
              <strong>R$ {{ sum.total_irrf | formatDouble }}</strong>
            </td>
            <td align="right" v-if="particular">
              <strong>R$ {{ sum.total_irrf_decimo_terceiro | formatDouble }}</strong>
            </td>
            <td align="right" v-if="particular">
              <strong>R$ {{ sum.total_contribuicao_previdenciaria | formatDouble }}</strong>
            </td>
          </template>
          <template slot="pageText" slot-scope="props">
            {{ props.pageStart }} - {{ props.pageStop }} de
            {{ props.itemsLength }}
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <!--Create Edit-->
    <v-dialog v-model="dialog" max-width="1600px" persistent>
      <v-card>
        <v-card-text>
          <v-container grid-list-md fluid>
            <v-layout wrap justify-right align-right>
              <v-flex xs12>
                <span class="display-1">{{ formTitle }}</span>
              </v-flex>
              <v-flex xs12 md8>
                <v-text-field
                  name="id_texto"
                  v-model="editedItem.id_texto"
                  counter
                  maxlength="60"
                  v-validate="editedItem.codigo !== 25 ? 'required' : ''"
                  data-vv-validate-on="blur"
                  :error-messages="errors.collect('id_texto')"
                  :data-vv-as="translate('table.' + entity + '.field.id_texto')"
                  :label="translate('table.' + entity + '.field.id_texto')"
                  :disabled="isTextoDisabled"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md4>
                <v-autocomplete
                  name="codigo"
                  v-model="editedItem.codigo"
                  data-vv-validate-on="blur"
                  v-validate="'required'"
                  :error-messages="errors.collect('codigo')"
                  :data-vv-as="translate('table.' + entity + '.field.codigo')"
                  :label="translate('table.' + entity + '.field.codigo')"
                  :items="
                    particular
                      ? translate('value.rend_isento_codigo_particular')
                      : translate('value.rend_isento_codigo_condominio')
                  "
                  @change="processFields"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs12 md6>
                <v-text-field
                  name="sequencia_ir"
                  data-vv-validate-on="blur"
                  v-model="editedItem.sequencia_ir"
                  counter
                  maxlength="19"
                  v-validate="'required'"
                  @blur="checkDuplicate(items, 'sequencia_ir', editedItem.sequencia_ir)"
                  :error-messages="errors.collect('sequencia_ir')"
                  :data-vv-as="translate('table.' + entity + '.field.sequencia_ir')"
                  :label="translate('table.' + entity + '.field.sequencia_ir')"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md6>
                <p-cpf-cnpj
                  name="cnpj"
                  v-model="editedItem.cnpj"
                  v_validate="required"
                  :disabled="isTextoDisabled"
                ></p-cpf-cnpj>
              </v-flex>
              <v-flex xs12 v-if="temDependente">
                <v-autocomplete
                  :label="translate('table.' + entity + '.field.pessoa_dependente_id')"
                  v-model="editedItem.pessoa_dependente_id"
                  :items="dependente_items"
                  item-text="id_texto"
                  item-value="id"
                ></v-autocomplete>
              </v-flex>

              <!--INICIO------------------- Distribuição ------------------->
              <v-flex xs12 md8>
                <span class="headline">Distribuições</span>
              </v-flex>
              <v-flex xs12 md4 class="text-xs-right">
                <v-btn color="primary" @click="createSubItem()" class="my-1">
                  {{ this.translate("system.button.add.title") }}
                  <v-icon dark>
                    {{ this.translate("system.button.add.icon") }}
                  </v-icon>
                </v-btn>
              </v-flex>
              <v-flex xs12>

                <!-- Código diferente de 10 e 11 -->
                <v-data-table
                  :headers="sub_headers"
                  :items="editedItem.sub_items"
                  id="sub_items_table"
                  hide-actions
                  no-data-text="Não existem registros"
                  no-results-text="Nenhum registro encontrado"
                  v-show="(editedItem.codigo !== 10) && (editedItem.codigo !== 11)"
                >
                  <template slot="items" slot-scope="props">
                    <td>
                      <p-date limitPreviousYears v-model="props.item.data"></p-date>
                    </td>
                    <td>
                      <p-money
                        name="valor"
                        v-model="props.item.valor"
                        :v_validate="'positiveNumber'"
                        data_vv_as="Valor"
                      ></p-money>
                    </td>
                    <td align="center">
                      <v-btn icon class="mx-0" @click="deleteSubItem(props.item)">
                        <v-icon color="pink">delete</v-icon>
                      </v-btn>
                    </td>
                  </template>
                </v-data-table>

                <!-- Código 10 -->
                <v-data-table
                  :headers="sub_headers_cod_10"
                  :items="editedItem.sub_items"
                  id="sub_items_table"
                  hide-actions
                  no-data-text="Não existem registros"
                  no-results-text="Nenhum registro encontrado"
                  v-show="editedItem.codigo == 10"
                >
                  <template slot="items" slot-scope="props">
                    <td>
                      <p-date limitPreviousYears v-model="props.item.data"></p-date>
                    </td>
                    <td>
                      <p-money
                        name="valor"
                        v-model="props.item.valor"
                        :v_validate="'positiveNumber'"
                        data_vv_as="Valor"
                      ></p-money>
                    </td>
                    <td>
                      <p-money
                        name="decimo_terceiro"
                        v-model="props.item.decimo_terceiro"
                        :v_validate="'positiveNumber'"
                        data_vv_as="decimo_terceiro"
                        class="money-grid"
                      ></p-money>
                    </td>
                    <td align="center">
                      <v-btn icon class="mx-0" @click="deleteSubItem(props.item)">
                        <v-icon color="pink">delete</v-icon>
                      </v-btn>
                    </td>
                  </template>
                </v-data-table>

                <!-- Código 11 -->
                <v-data-table
                  :headers="sub_headers_cod_11"
                  :items="editedItem.sub_items"
                  id="sub_items_table"
                  hide-actions
                  no-data-text="Não existem registros"
                  no-results-text="Nenhum registro encontrado"
                  v-show="editedItem.codigo == 11"
                >
                  <template slot="items" slot-scope="props">
                    <td>
                      <p-date limitPreviousYears v-model="props.item.data"></p-date>
                    </td>
                    <td>
                      <p-money
                        name="valor"
                        v-model="props.item.valor"
                        :v_validate="'positiveNumber'"
                        data_vv_as="valor"
                      ></p-money>
                    </td>
                    <td>
                      <p-money
                        name="decimo_terceiro"
                        v-model="props.item.decimo_terceiro"
                        :v_validate="'positiveNumber'"
                        data_vv_as="decimo_terceiro"
                        class="money-grid"
                      ></p-money>
                    </td>
                    <td>
                      <p-money
                        name="irrf"
                        v-model="props.item.irrf"
                        :v_validate="'positiveNumber'"
                        data_vv_as="irrf"
                        class="money-grid"
                      ></p-money>
                    </td>
                    <td>
                      <p-money
                        name="irrf_decimo_terceiro"
                        v-model="props.item.irrf_decimo_terceiro"
                        :v_validate="'positiveNumber'"
                        data_vv_as="irrf_decimo_terceiro"
                        class="money-grid"
                      ></p-money>
                    </td>
                    <td>
                      <p-money
                        name="contribuicao_previdenciaria"
                        v-model="props.item.contribuicao_previdenciaria"
                        :v_validate="'positiveNumber'"
                        data_vv_as="contribuicao_previdenciaria"
                        class="money-grid"
                      ></p-money>
                    </td>
                    <td align="center">
                      <v-btn icon class="mx-0" @click="deleteSubItem(props.item)">
                        <v-icon color="pink">delete</v-icon>
                      </v-btn>
                    </td>
                  </template>
                </v-data-table>
              </v-flex>

              <!-- Totalizadores -->
              <!-- Rendimento Total -->
              <v-flex xs12 md4>
                <p-money
                  name="valor"
                  label="Rendimento Total"
                  v-model="editedItemSum.valor"
                  :v_validate="'positiveNumber'"
                  disabled
                ></p-money>
              </v-flex>

              <!-- Total do 13º -->
              <v-flex xs12 md2 v-show="(editedItem.codigo == 11) || (editedItem.codigo == 10)">
                <p-money
                  name="decimo_terceiro"
                  label="Total do 13º"
                  v-model="editedItemSum.decimo_terceiro"
                  :v_validate="'positiveNumber'"
                  disabled
                ></p-money>
              </v-flex>

              <!-- Total do IRRF -->
              <v-flex xs12 md2 v-show="editedItem.codigo == 11">
                <p-money
                  name="irrf"
                  label="Total do IRRF"
                  v-model="editedItemSum.irrf"
                  :v_validate="'positiveNumber'"
                  disabled
                ></p-money>
              </v-flex>

              <!-- Total do IRRF sobre o 13º -->
              <v-flex xs12 md2 v-show="editedItem.codigo == 11">
                <p-money
                  name="irrf_decimo_terceiro"
                  label="Total do IRRF sobre o 13º"
                  v-model="editedItemSum.irrf_decimo_terceiro"
                  :v_validate="'positiveNumber'"
                  disabled
                ></p-money>
              </v-flex>

              <!-- Total da Contribuição Previdenciária -->
              <v-flex xs12 md2 v-show="editedItem.codigo == 11">
                <p-money
                  name="contribuicao_previdenciaria"
                  label="Total da Contribuição Previdenciária"
                  v-model="editedItemSum.contribuicao_previdenciaria"
                  :v_validate="'positiveNumber'"
                  disabled
                ></p-money>
              </v-flex>
              <!--FIM------------------- Totalizadores ------------------>
              <!--FIM------------------- Distribuição ------------------->

              <v-flex xs12>
                <v-btn class="mx-1" color="primary darken-2" :disabled="saving" :loading="saving" @click.native="save()">Salvar</v-btn>
                <v-btn class="mx-1" @click.native="close(400)">Cancelar</v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import crud from "./../../mixin/crud";
import PDate from "../shared/PDate";
import PMoney from "../shared/PMoney";
import Helper from "./../../classes/helper";
import acl from "./../../mixin/acl";
import PCpfCnpj from "../shared/PCpfCnpj";

export default {
  components: {
    PDate,
    PMoney,
    PCpfCnpj,
  },
  mixins: [crud, acl],
  data: () => ({
    isTextoDisabled: false,
    isCnpjDisabled: false,
    entity: "condominio_rendimentoisento",
    items: [],
    sum: {
      total_valor: 0,
      total_contribuicao_previdenciaria: 0,
      total_irrf: 0,
      total_decimo_terceiro: 0,
      total_irrf_decimo_terceiro: 0,
    },
    sequencia_ir_duplicated: false,
  }),
  name: "RendimentoIsento",
  props: ["condominio_id", "values", "initials", "temDependente", "dependente_items", "particular"],
  watch: {
    items(newValue, oldValue) {
      this.updateSum();
    },
  },
  created() {
    this.items = this.values;
    this.editedItem = {
      condominio_id: this.condominio_id,
      ativo: true,
      sub_items: [],
      pessoa_dependente_id: null,
      cnpj: "",
      codigo: null,
    };
    this.defaultItem = {
      condominio_id: this.condominio_id,
      ativo: true,
      sub_items: [],
      pessoa_dependente_id: null,
      cnpj: "",
      codigo: null,
    };
  },
  computed: {
    headers () {
      let idTextWidth = 50 -(this.temDependente ? 10 : 0) -(this.particular ? 20 : 0)
      let result = [
        {
          text: this.translate("table." + this.entity + ".field.sequencia_ir"),
          value: "sequencia_ir",
          width: "10%",
        },
        {
          text: this.translate("table." + this.entity + ".field.id_texto"),
          value: "id_texto",
          width: idTextWidth + "%",
        },
        {
          text: this.translate("table." + this.entity + ".field.codigo"),
          value: "codigo",
          width: "10%",
        },
        {
          text: this.translate("table." + this.entity + ".field.cnpj"),
          value: "cnpj",
          width: "10%",
        },
        {
          text: this.translate("table." + this.entity + ".field.valor"),
          value: "valor",
          width: "10%",
          align: "right",
        }];
      
      // Adicionar os valores que so possuem no contribuinte
      if (this.particular) {
        result.push(...[{
          text: this.translate("table." + this.entity + ".field.decimo_terceiro"),
          value: "decimo_terceiro",
          width: "5%",
          align: "right",
        },
        {
          text: this.translate("table." + this.entity + ".field.irrf"),
          value: "irrf",
          width: "5%",
          align: "right",
        },
        {
          text: this.translate("table." + this.entity + ".field.irrf_decimo_terceiro"),
          value: "irrf_decimo_terceiro",
          width: "5%",
          align: "right",
        },
        {
          text: this.translate("table." + this.entity + ".field.contribuicao_previdenciaria"),
          value: "contribuicao_previdenciaria",
          width: "5%",
          align: "right",
        }]);
      }
      // Adicionar o dependente caso tenha
      if (this.temDependente) {
        result.push(...[{
            text: this.translate("table." + this.entity + ".field.pessoa_dependente_id"),
            value: "dependente.id_texto",
            width: "10%",
          }]);
      }

      // Adicionar as ações no final
      result.push(...[{
        text: this.translate("system.table.action"),
        value: "action",
        sortable: false,
        align: "center",
        width: "10%",
      }]);

      return result;
    },
    sub_headers: function () {
      return [
        {
          text: this.translate("table." + this.entity + "_item.field.data"),
          sortable: false,
          value: "data",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.rendimento"),
          sortable: false,
          value: "valor",
        },
        {
          text: this.translate("system.table.action"),
          value: "action",
          sortable: false,
          align: "center",
        },
      ];
    },
    sub_headers_cod_10: function () {
      return [
        {
          text: this.translate("table." + this.entity + "_item.field.data"),
          sortable: false,
          value: "data",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.rendimento"),
          sortable: false,
          value: "valor",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.decimo_terceiro"),
          sortable: false,
          value: "decimo_terceiro",
        },
        {
          text: this.translate("system.table.action"),
          value: "action",
          sortable: false,
          align: "center",
        },
      ];
    },
    sub_headers_cod_11: function () {
      return [
        {
          text: this.translate("table." + this.entity + "_item.field.data"),
          sortable: false,
          value: "data",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.rendimento"),
          sortable: false,
          value: "valor",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.decimo_terceiro"),
          sortable: false,
          value: "decimo_terceiro",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.irrf"),
          sortable: false,
          value: "irrf",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.irrf_decimo_terceiro"),
          sortable: false,
          value: "irrf_decimo_terceiro",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.contribuicao_previdenciaria"),
          sortable: false,
          value: "contribuicao_previdenciaria",
        },
        {
          text: this.translate("system.table.action"),
          value: "action",
          sortable: false,
          align: "center",
        },
      ];
    },
    editedItemSum() {
      return this.editedItem.sub_items.reduce(
        (acc, cur) => {
          acc["valor"] += cur["valor"];
          acc["contribuicao_previdenciaria"] += cur["contribuicao_previdenciaria"];
          acc["irrf"] += cur["irrf"];
          acc["decimo_terceiro"] += cur["decimo_terceiro"];
          acc["irrf_decimo_terceiro"] += cur["irrf_decimo_terceiro"];
          return acc;
        },
        {
          valor: 0,
          contribuicao_previdenciaria: 0,
          irrf: 0,
          decimo_terceiro: 0,
          irrf_decimo_terceiro: 0,
        }
      );
    },
  },
  methods: {
    processFields() {
      if ([20, 21, 23, 24, 25].includes(this.editedItem.codigo)) {
        this.editedItem.id_texto = "";
        this.isTextoDisabled = true;
        this.editedItem.cnpj = "";
        this.isCnpjDisabled = true;
      } else {
        this.isTextoDisabled = false;
        this.isCnpjDisabled = false;
      }
    },
    checkDuplicate(items, index, value) {
      // this.sequencia_ir_duplicated = items.find((item) => {
      //   return item[index] === value;
      // });
      // if (this.sequencia_ir_duplicated) {
      //   event.preventDefault();
      //   event.target.select();
      // }
    },
    beforeCreate() {
      this.editedItem.sequencia_ir = Helper.generateSequenciaIr(this.initials, "RI", this.values);
    },
    createSubItem: function () {
      this.editedItem.sub_items.push({
        tipo: "pagamento",
        data: "",
        valor: 0,
      });
    },
    deleteSubItem(item) {
      const index = this.editedItem.sub_items.indexOf(item);
      this.editedItem.sub_items.splice(index, 1);
    },
    afterSave() {
      this.updateSum()
    },
    updateSum() {
      this.sum = this.$refs.RendimentoIsentoTable.filteredItems.reduce(
        (acc, item) => {
          const total = item.sub_items.reduce(
            (acc2, sub_item) => {
              acc2.total_valor += sub_item.valor;
              acc2.total_contribuicao_previdenciaria += sub_item.contribuicao_previdenciaria;
              acc2.total_irrf += sub_item.irrf;
              acc2.total_decimo_terceiro += sub_item.decimo_terceiro;
              acc2.total_irrf_decimo_terceiro += sub_item.irrf_decimo_terceiro;
              return acc2;
            },
            {
              total_valor: 0,
              total_contribuicao_previdenciaria: 0,
              total_irrf: 0,
              total_decimo_terceiro: 0,
              total_irrf_decimo_terceiro: 0,
            }
          );
          acc.total_valor += total.total_valor;
          acc.total_contribuicao_previdenciaria += total.total_contribuicao_previdenciaria;
          acc.total_irrf += total.total_irrf;
          acc.total_decimo_terceiro += total.total_decimo_terceiro;
          acc.total_irrf_decimo_terceiro += total.total_irrf_decimo_terceiro;

          item.total_valor = total.total_valor;
          item.total_contribuicao_previdenciaria = total.total_contribuicao_previdenciaria;
          item.total_irrf = total.total_irrf;
          item.total_decimo_terceiro = total.total_decimo_terceiro;
          item.total_irrf_decimo_terceiro = total.total_irrf_decimo_terceiro;

          return acc;
        },
        {
          total_valor: 0,
          total_contribuicao_previdenciaria: 0,
          total_irrf: 0,
          total_decimo_terceiro: 0,
          total_irrf_decimo_terceiro: 0,
        }
      );

      console.log(this.sum);
    },
    getCodigoText (codigo) {
      const translate = this.particular ? 'value.rend_isento_codigo_particular' : 'value.rend_isento_codigo_condominio'
      const item = this.translate(translate).find(c => c.value === codigo)
      return item ? item.text : ''
    },
    // type 1 = pdf 2 = excel
    exportReport (type) {
      this.$store.commit("loader/change_dialog", true);
      this.$http.put(`report/report_details/${this.condominio_id}`, {"type": type, "table": "condominio_rendimentoisento", "search": this.search, "apenasAtivos": this.apenasAtivos, "isCondominio": !this.particular}).then(
        (res) => {
          let fileName = res.data;
          window.open(process.env.API + "reports/" + fileName, "_blank");
          this.$store.commit("loader/change_dialog", false);
          setTimeout(function () {
            _this.$http.delete(`report/delete_upload_report/${fileName}`)
          }, 5000)
        },
        (error) => {
          this.$store.commit("loader/change_dialog", false);
          this.$store.commit("snackbar/change_values", {
            text:
              error.body.message ||
              "Desculpe, não conseguimos gerar o relatório no momento. Por favor, tente novamente mais tarde.",
            color: "error",
            dialog: true,
          });
        }
      );
    }
  },
};
</script>

<style scoped>
#sub_items_table {
  border-radius: 4px;
  border: 1px solid #bbb;
  margin-bottom: 4px;
}

#sub_items_table .custom-height {
  padding-top: 8px !important;
  margin-bottom: -28px !important;
}

td .custom-height {
  padding-top: 6px;
  display: flex;
  align-items: center;
}

#proccess {
  position: absolute;
}

#proccess .v-icon {
  position: relative;
  bottom: 50px;
  left: 233px;
}

.money_input{
  margin-top: 14px;
}
</style>



// WEBPACK FOOTER //
// src/components/details/RendimentoIsento.vue